import React, { useContext, useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form, Modal } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { UserService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Switch } from "../../shared/components/Switch";
import { EnumsContext } from "../../context/EnumsContext";
import Select from "react-select";
import FormDropzone from "../../shared/dropzone";
import CheckboxTree from "react-checkbox-tree";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import { Editor } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";
import { ISelect } from "../interfaces";
import { CompanyService } from "../company/service";
import { QualificationsService } from "../qualifications/service";
import { JobTargetGroupsService } from "../jobTargetGroups/service";
import { LocationService } from "../location/service";
import { JobCategoriesService } from "../jobCategories/service";
import DatePicker from "react-datepicker";
import moment from "moment";
import { Accordion } from "react-bootstrap";

// Validation schema with role-based conditional validation
const schema = yup.object({
  // Roles field is always required
  roles: yup
    .array()
    .of(yup.string())
    .min(1, "formErrors.rolesLength")
    .required("formErrors.roleRequired"),

  // Basic fields are required when roles are selected
  firstName: yup.string().when("roles", {
    is: (roles: string[]) => roles && roles.length > 0,
    then: (schema) => schema.required("formErrors.firstNameRequired"),
    otherwise: (schema) => schema.optional(),
  }),
  lastName: yup.string().when("roles", {
    is: (roles: string[]) => roles && roles.length > 0,
    then: (schema) => schema.required("formErrors.lastNameRequired"),
    otherwise: (schema) => schema.optional(),
  }),
  email: yup
    .string()
    .email("formErrors.invalidEmail")
    .when("roles", {
      is: (roles: string[]) => roles && roles.length > 0,
      then: (schema) => schema.required("formErrors.emailRequired"),
      otherwise: (schema) => schema.optional(),
    }),
  isActive: yup.boolean().when("roles", {
    is: (roles: string[]) => roles && roles.length > 0,
    then: (schema) => schema.required("formErrors.statusRequired"),
    otherwise: (schema) => schema.optional(),
  }),

  // Conditionally required based on roles
  about: yup.string().when("roles", {
    is: (roles: string[]) =>
      roles?.includes("ROLE_USER") || roles?.includes("ROLE_ADMIN"),
    then: (schema) => schema.required("formErrors.aboutRequired"),
    otherwise: (schema) => schema.optional(),
  }),

  locationType: yup
    .array()
    .of(yup.string())
    .when("roles", {
      is: (roles: string[]) =>
        roles?.includes("ROLE_USER") || roles?.includes("ROLE_ADMIN"),
      then: (schema) => schema.min(1, "formErrors.locationTypeRequired"),
      otherwise: (schema) => schema.optional(),
    })
    .default([]),

  employmentType: yup
    .string()
    .when("roles", {
      is: (roles: string[]) =>
        roles?.includes("ROLE_USER") || roles?.includes("ROLE_ADMIN"),
      then: (schema) => schema.required("formErrors.employmentTypeRequired"),
      otherwise: (schema) => schema.optional().nullable(),
    })
    .default(null),

  targetGroups: yup
    .array()
    .of(yup.number())
    .when("roles", {
      is: (roles: string[]) => roles?.includes("ROLE_USER"),
      then: (schema) => schema.min(1, "formErrors.targetGroupsRequired"),
      otherwise: (schema) => schema.optional(),
    })
    .default([]),

  videoPresentation: yup
    .string()
    .url("formErrors.invalidUrl")
    .nullable()
    .optional(),

  qualifications: yup.array().of(yup.number()).default([]),

  company: yup.number().nullable().optional(),

  // Contact fields flattened to root level for better validation
  phone: yup.string().when("roles", {
    is: (roles: string[]) => roles?.includes("ROLE_ADMIN"),
    then: (schema) => schema.required("formErrors.phoneRequired"),
    otherwise: (schema) => schema.optional(),
  }),
  city: yup.string().nullable().optional(),
  zip: yup.string().optional(),
  address: yup.string().nullable().optional(),
  addressSuffix: yup.string().optional(),

  // Keep contact object for backwards compatibility in the form
  contact: yup
    .object()
    .shape({
      phone: yup.string().optional(),
      city: yup.string().optional(),
      zip: yup.string().optional(),
      address: yup.string().optional(),
      addressSuffix: yup.string().optional(),
    })
    .optional(),

  password: yup.string().when("$isEdit", ([isEdit], schema) => {
    return isEdit
      ? schema.optional()
      : schema.required("formErrors.passwordRequired");
  }),

  image: yup.mixed().nullable().optional(),

  locations: yup
    .array()
    .of(yup.number())
    .when(["locationType", "roles"], {
      is: (locationType: string[], roles: string[]) => {
        const hasCoachOrAdmin =
          roles?.includes("ROLE_USER") || roles?.includes("ROLE_ADMIN");
        const hasOfflineOrHybrid =
          locationType?.includes("offline") || locationType?.includes("hybrid");
        return hasCoachOrAdmin && hasOfflineOrHybrid;
      },
      then: (schema) => schema.min(1, "formErrors.locationsRequired"),
      otherwise: (schema) => schema.optional(),
    })
    .default([]),

  teachingLanguages: yup
    .array()
    .of(
      yup.object({
        language: yup.string().required("formErrors.languageRequired"),
        skillLevel: yup
          .string()
          .required("formErrors.proficiencyLevelRequired"),
      }),
    )
    .when("roles", {
      is: (roles: string[]) =>
        roles?.includes("ROLE_USER") || roles?.includes("ROLE_ADMIN"),
      then: (schema) => schema.min(1, "formErrors.teachingLanguageRequired"),
      otherwise: (schema) => schema.optional(),
    })
    .default([]),

  jobCategories: yup
    .array()
    .of(yup.number())
    .when("roles", {
      is: (roles: string[]) =>
        roles?.includes("ROLE_USER") || roles?.includes("ROLE_ADMIN"),
      then: (schema) => schema.min(1, "formErrors.categoryRequired"),
      otherwise: (schema) => schema.optional(),
    })
    .default([]),

  userCareerItems: yup
    .array()
    .of(
      yup.object({
        startDate: yup.string().required("formErrors.startDateRequired"),
        endDate: yup.string().nullable().optional(),
        location: yup.string().required("formErrors.locationRequired"),
        description: yup.string().nullable().optional(),
      }),
    )
    .default([])
    .optional(),

  minimumHourlyWage: yup.number().when("roles", {
    is: (roles: string[]) =>
      roles?.includes("ROLE_USER") || roles?.includes("ROLE_ADMIN"),
    then: (schema) =>
      schema
        .positive("formErrors.invalidRate")
        .required("formErrors.minimumHourlyRateRequired"),
    otherwise: (schema) => schema.optional(),
  }),

  experience: yup.string().when("roles", {
    is: (roles: string[]) =>
      roles?.includes("ROLE_USER") || roles?.includes("ROLE_ADMIN"),
    then: (schema) => schema.required("formErrors.experienceRequired"),
    otherwise: (schema) => schema.optional(),
  }),

  documents: yup.array().optional(),
});

// Interfaces
// Infer UserState type from the yup schema
type UserState = yup.InferType<typeof schema> & {
  documents?: {
    id: number;
    path: string;
    file: {
      name: string;
      nameOriginal: string;
      mime: string;
      size: number;
    };
  }[];
};
interface CategoryNode {
  value: string;
  label: string;
  children?: CategoryNode[];
}

const UserForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [companyList, setCompanyList] = useState<ISelect[]>([]);
  const [qualificationList, setQualificationList] = useState<ISelect[]>([]);
  const [categoryList, setCategoryList] = useState<CategoryNode[]>([]);
  const [targetGroupList, setTargetGroupList] = useState<ISelect[]>([]);
  const [checked, setChecked] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [userData, setUserData] = useState<UserState | null>(null);
  const [documents, setDocuments] = useState<any[]>([]); // Unified state for documents
  const [documentToDelete, setDocumentToDelete] = useState<{
    id?: number;
    path?: string;
    file?: File | null;
  } | null>(null); // State for document to delete
  const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing the delete confirmation modal
  const [locationTree, setLocationTree] = useState<CategoryNode[]>([]); // Tree structure for locations
  const [checkedLocations, setCheckedLocations] = useState<string[]>([]); // Checked locations
  const [expandedLocations, setExpandedLocations] = useState<string[]>([]); // Expanded locations
  const [activeAccordionKey, setActiveAccordionKey] = useState<string | null>(
    "0",
  ); // State to manage active accordion key
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<UserState>({
    resolver: yupResolver(schema),
    context: { isEdit },
    mode: isEdit ? "onChange" : "onSubmit", // Edit: validate on change, Create: validate on submit
    reValidateMode: "onChange", // After first submit, validate on every change
    defaultValues: {
      firstName: "",
      lastName: "",
      about: undefined,
      email: "",
      locationType: [],
      employmentType: undefined,
      targetGroups: [],
      qualifications: [],
      isActive: true,
      roles: [],
      company: undefined,
      // Flattened contact fields
      phone: "",
      city: "",
      zip: "",
      address: "",
      addressSuffix: "",
      // Keep contact object for backwards compatibility
      contact: {
        phone: "",
        city: "",
        zip: "",
        address: "",
        addressSuffix: "",
      },
      password: undefined,
      image: undefined,
      locations: [],
      teachingLanguages: [],
      jobCategories: [],
      userCareerItems: [],
      minimumHourlyWage: undefined,
      experience: undefined,
    },
  });

  // Watch the roles field to conditionally render fields
  const selectedRoles: string[] = (watch("roles") as string[]) || [];

  // Watch jobCategories and locations to sync with local state
  const watchedCategories = watch("jobCategories") || [];
  const watchedLocations = watch("locations") || [];

  // Re-trigger validation whenever roles change, but only after first submit
  useEffect(() => {
    if (selectedRoles.length > 0 && (isEdit || isSubmitted)) {
      // Trigger validation for all fields to update required/optional status
      trigger();
    }
  }, [selectedRoles, trigger, isEdit, isSubmitted]);

  // Sync local states with form values
  useEffect(() => {
    if (watchedCategories.length > 0) {
      setChecked(
        watchedCategories
          .filter((id: number | undefined) => id !== undefined)
          .map((id: number | undefined) => id!.toString()),
      );
    }
  }, [watchedCategories]);

  useEffect(() => {
    if (watchedLocations.length > 0) {
      setCheckedLocations(
        watchedLocations
          .filter((id: number | undefined) => id !== undefined)
          .map((id: number | undefined) => id!.toString()),
      );
    }
  }, [watchedLocations]);

  // Helper functions to determine what fields to show based on roles
  const isCompanyAdmin = () => selectedRoles.includes("ROLE_COMPANY_ADMIN");
  const isCompanyUser = () => selectedRoles.includes("ROLE_COMPANY_USER");
  const isCoach = () => selectedRoles.includes("ROLE_USER");
  const isAdmin = () => selectedRoles.includes("ROLE_ADMIN");

  // Helper function to determine when to show errors
  // Show errors if: it's edit mode OR form has been submitted at least once
  const shouldShowError = (fieldError: any) =>
    (isEdit || isSubmitted) && fieldError;

  // Field visibility logic - only show fields when roles are selected
  const hasRoles = () => selectedRoles.length > 0;
  const showBasicFields = () => hasRoles(); // Show basic fields only when roles are selected
  const showAddressSuffix = () =>
    hasRoles() && (isCompanyAdmin() || isCoach() || isAdmin());
  const showLocationTypes = () => hasRoles() && (isCoach() || isAdmin());
  const showEmploymentType = () => hasRoles() && (isCoach() || isAdmin());
  const showQualifications = () => hasRoles() && (isCoach() || isAdmin());
  const showTargetGroups = () => hasRoles() && (isCoach() || isAdmin());
  const showAbout = () => hasRoles() && (isCoach() || isAdmin());
  const showTeachingLanguages = () => hasRoles() && (isCoach() || isAdmin());
  const showCareerItems = () => hasRoles() && (isCoach() || isAdmin());
  const showCategories = () => hasRoles() && (isCoach() || isAdmin());
  const showLocations = () => hasRoles() && (isCoach() || isAdmin());
  const showVideoPresentation = () => hasRoles() && (isCoach() || isAdmin());
  const showStatusToggle = () => hasRoles(); // Show status toggle for all user types
  const showDocuments = () => hasRoles() && (isCoach() || isAdmin());
  const showMinimumHourlyWage = () => hasRoles() && (isCoach() || isAdmin());
  const showExperience = () => hasRoles() && (isCoach() || isAdmin());
  const showCompanyField = () => hasRoles(); // Show company field when roles are selected
  const showContactFields = () => hasRoles(); // Show contact fields when roles are selected
  const showPasswordField = () => hasRoles(); // Show password field when roles are selected

  const { enums, loading } = useContext(EnumsContext);
  const userEnums = enums.userRole;
  const teachingLanguage = enums.teachingLanguage?.map((item: any) => ({
    label: t(`user.teachingLanguage.${item}`),
    value: item,
  }));
  const languageSkillLevel = enums.languageSkillLevel?.map((item: any) => ({
    label: t(`user.languageSkillLevel.${item}`),
    value: item,
  }));

  const jobLocationType = enums.jobLocationType?.map((item: any) => ({
    label: item[0].toUpperCase() + item.slice(1),
    value: item,
  }));

  const employmentTypeOptions = (
    enums.employmentType || ["all", "employed", "freelance"]
  ).map((item: any) => ({
    label: t(`enums.employmentType.${item}`),
    value: item,
  }));

  const yearsOfExperienceOptions = enums.yearsOfExperience?.map(
    (item: any) => ({
      label: t(`enums.yearsOfExperience.${item}`),
      value: item,
    }),
  );

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companies, qualifications, targetGroups, jobCategories] =
          await Promise.all([
            CompanyService.getAll({ perPage: 0, page: 1 }),
            QualificationsService.getAll({ perPage: 0, page: 1 }),
            JobTargetGroupsService.getAll({ perPage: 0, page: 1 }),
            JobCategoriesService.getAll(),
          ]);
        setCompanyList(
          companies.data.map((comp: any) => ({
            label: comp.name,
            value: comp.id,
          })),
        );
        setQualificationList(
          qualifications.data.map((qual: any) => ({
            label: qual.name,
            value: qual.id,
          })),
        );
        setTargetGroupList(
          targetGroups.data.map((tg: any) => ({
            label: tg.title,
            value: tg.id,
          })),
        );

        const processCategories = (
          categories: any[],
          level = 1,
        ): CategoryNode[] => {
          if (level > 3) return [];
          return categories.map((category) => ({
            value: category.id.toString(),
            label: category.title,
            children:
              level < 3 && category.children
                ? processCategories(category.children, level + 1)
                : undefined,
          }));
        };
        setCategoryList(processCategories(jobCategories));
      } catch (err) {
        ErrorToast(err);
      }
    };

    fetchData();
  }, []);

  // Fetch location data as a tree
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await LocationService.getTree({ perPage: 0, page: 1 });
        const processLocations = (locations: any[]): CategoryNode[] => {
          return locations.map((loc) => ({
            value: loc.id.toString(),
            label: loc.name,
            children: loc.children ? processLocations(loc.children) : undefined,
          }));
        };
        setLocationTree(processLocations(response));
      } catch (error) {
        ErrorToast(error);
      }
    };

    fetchLocations();
  }, []);

  // Handle user data
  useEffect(() => {
    if (isEdit) {
      UserService.getSingle(+id!)
        .then((response) => {
          const user = response;

          setUserData({
            firstName: user.firstName,
            lastName: user.lastName,
            about: user.about ?? "",
            // teachingMethodAndPhilosophy: user.teachingMethodAndPhilosophy,
            email: user.email,
            documents: user.documents,
            isActive: user.isActive,
            roles: user.roles,
            company: user?.company?.id,
            locationType: user?.locationType || [],
            employmentType: user?.employmentType || undefined,
            targetGroups: user?.targetGroups?.map((tg: any) => tg.id) || [],
            qualifications: user?.qualifications?.map((q: any) => q.id) || [],
            // Flattened contact fields for validation
            phone: user.contact.phone || undefined,
            city: user.contact.city,
            zip: user.contact.zip,
            address: user.contact.address,
            addressSuffix: user.contact.addressSuffix,
            // Keep contact object for backwards compatibility
            contact: {
              phone: user.contact.phone || undefined,
              city: user.contact.city,
              zip: user.contact.zip,
              address: user.contact.address,
              addressSuffix: user.contact.addressSuffix,
            },
            password: "",
            image: user.profilePicture,
            locations: user?.locations?.map((loc: any) => loc.id) || [],
            teachingLanguages: user.teachingLanguages.map((lang: any) => ({
              language: lang.language,
              skillLevel: lang.skillLevel,
            })),
            jobCategories: user?.jobCategories?.map((cat: any) => cat.id) || [],
            userCareerItems:
              user?.userCareerItems?.map((item: any) => ({
                startDate: item.startDate,
                endDate: item.endDate,
                location: item.location,
                description: item.description,
              })) || [], // Initialize userCareerItems
            minimumHourlyWage: user?.minimumHourlyWage || undefined, // Initialize minimumHourlyWage
            experience: user?.experience || undefined, // Initialize experience
            videoPresentation: user?.videoPresentation || undefined, // Initialize videoPresentation
          });
        })
        .catch((error) => {
          ErrorToast(error.message || error);
        });
    }
  }, [isEdit, id]);

  // Sync locations with checkedLocations
  useEffect(() => {
    if (userData?.locations) {
      setCheckedLocations(
        userData.locations
          .filter((id) => id !== undefined)
          .map((id) => id!.toString()),
      ); // Convert IDs to strings
    }
  }, [userData]);

  // Reset form when userData is loaded
  useEffect(() => {
    if (userData) {
      reset({
        ...userData,
        teachingLanguages: userData?.teachingLanguages || [],
      });

      // Sync the checked categories with form data
      if (userData.jobCategories) {
        setChecked(
          userData.jobCategories
            .filter((id) => id !== undefined)
            .map((id) => id!.toString()),
        );
      }

      // Sync the checked locations with form data
      if (userData.locations) {
        setCheckedLocations(
          userData.locations
            .filter((id) => id !== undefined)
            .map((id) => id!.toString()),
        );
      }
    }
  }, [userData, reset]);

  // Handle expanded and checked states for categories
  useEffect(() => {
    if (userData && categoryList.length > 0) {
      const checkedCategories = (userData.jobCategories || [])
        .filter((id) => id !== undefined)
        .map((id) => id!.toString());
      setChecked(checkedCategories);

      const findParentNodes = (
        categories: CategoryNode[],
        checked: string[],
      ): string[] => {
        const expanded: string[] = [];
        const traverse = (nodes: CategoryNode[], parents: string[] = []) => {
          nodes.forEach((node) => {
            if (checked.includes(node.value)) {
              expanded.push(...parents);
            }
            if (node.children) {
              traverse(node.children, [...parents, node.value]);
            }
          });
        };
        traverse(categories);
        return Array.from(new Set(expanded));
      };

      const expandedCategories = findParentNodes(
        categoryList,
        checkedCategories,
      );
      setExpanded(expandedCategories);
    }
  }, [userData, categoryList]);

  useEffect(() => {
    if (userData && locationTree.length > 0) {
      const findParentNodes = (
        locations: CategoryNode[],
        checked: string[],
      ): string[] => {
        const expanded: string[] = [];
        const traverse = (nodes: CategoryNode[], parents: string[] = []) => {
          nodes.forEach((node) => {
            if (checked.includes(node.value)) {
              expanded.push(...parents);
            }
            if (node.children) {
              traverse(node.children, [...parents, node.value]);
            }
          });
        };
        traverse(locations);
        return Array.from(new Set(expanded));
      };

      const expandedLocations = findParentNodes(locationTree, checkedLocations);
      setExpandedLocations(expandedLocations);
    }
  }, [userData, locationTree, checkedLocations]);

  const handleImageUpload = (files: any) => {
    setValue("image", files);
  };

  const onDrop = (acceptedFiles: File[]) => {
    setDocuments(acceptedFiles);
  };

  const handleDeleteDocument = async () => {
    if (documentToDelete?.id) {
      try {
        // Call the API to delete the document
        await UserService.deleteDocument(documentToDelete.id);
        SuccessToast(t("user.documentDeleted"));
        const documents = getValues("documents") || [];
        // Remove the document from the state after successful API call

        setValue(
          "documents",
          documents.filter((doc) => doc.id !== documentToDelete.id),
        ); // Update the form state with the new documents array
      } catch (error) {
        ErrorToast(error); // Show error if the API call fails
      }
    }

    // Reset the state for the modal
    setDocumentToDelete(null);
    setShowDeleteModal(false); // Close the modal
  };

  // Load existing documents when editing
  useEffect(() => {
    if (isEdit && userData?.documents?.length) {
      setValue("documents", userData.documents); // Set the documents in the form state
    }
  }, [isEdit, userData]);

  const onSubmit = async (data: UserState) => {
    const formData = new FormData();

    // Sync flattened contact fields back to contact object
    const syncedData = {
      ...data,
      contact: {
        phone: data.phone || "",
        city: data.city || "",
        zip: data.zip || "",
        address: data.address || "",
        addressSuffix: data.addressSuffix || "",
      },
      experience: data.experience || undefined,
    };

    // Format userCareerItems dates
    const formattedCareerItems = (syncedData.userCareerItems || []).map(
      (item) => ({
        ...item,
        startDate: moment(item.startDate).format("YYYY-MM-DD"),
        endDate: moment(item.endDate).format("YYYY-MM-DD"),
      }),
    );

    // Append image if it exists
    if (
      syncedData.image &&
      Array.isArray(syncedData.image) &&
      syncedData.image[0]
    ) {
      formData.append("image", syncedData.image[0]);
    }

    // Append documents
    documents?.forEach((doc) => {
      formData.append("document", doc);
    });

    // Append other form data
    formData.append(
      "body",
      JSON.stringify({
        ...syncedData,
        userCareerItems: formattedCareerItems, // Use formatted dates
        // teachingMethodAndPhilosophy: stateToHTML(teachingMethodEditorState.getCurrentContent()),
      }),
    );

    try {
      isEdit
        ? await UserService.update(formData, +id!)
        : await UserService.create(formData);

      SuccessToast(t(`user.${isEdit ? "update" : "create"}`));
      navigate("/users"); // Navigate only after a successful API call
      reset();
    } catch (error) {
      ErrorToast(t(error as string));
    }
  };
  useEffect(() => {
    const teachingLanguages = getValues("teachingLanguages");
    // Only add empty teaching language entry for roles that need it (Coach/Admin)
    if (
      showTeachingLanguages() &&
      (!teachingLanguages || teachingLanguages.length === 0)
    ) {
      setValue("teachingLanguages", [{ language: "", skillLevel: "" }]);
    }
    // Clear teaching languages for roles that don't need them
    else if (
      !showTeachingLanguages() &&
      teachingLanguages &&
      teachingLanguages.length > 0
    ) {
      setValue("teachingLanguages", []);
    }
    // Re-trigger validation after modifying teaching languages, but only after first submit
    if (isEdit || isSubmitted) {
      trigger("teachingLanguages");
    }
  }, [getValues, setValue, selectedRoles, trigger, isEdit, isSubmitted]);

  if (loading) {
    return <div>{t("common.loading")}</div>;
  }

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card className="rounded-top-0 border-top-0">
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormErrorSummary errors={errors} />
                <Row className="g-3">
                  {/* Show help message for regular users */}
                  <Col sm={12}>
                    <div className="alert alert-info">
                      <div className="m-0">
                        <strong>{t("user.formHelp.userFormTitle")}</strong>{" "}
                        {t("user.formHelp.userFormDescription")}
                        <br />
                        <strong>
                          {t("user.formHelp.companySelectedTitle")}
                        </strong>{" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t(
                              "user.formHelp.companySelectedDescription",
                            ),
                          }}
                        />
                        <br />
                        <strong>
                          {t("user.formHelp.noCompanySelectedTitle")}
                        </strong>{" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t(
                              "user.formHelp.noCompanySelectedDescription",
                            ),
                          }}
                        />
                        <br />
                        <em>{t("user.formHelp.noteMessage")}</em>
                      </div>
                    </div>
                  </Col>
                  <Col sm={12}>
                    <div className="mb-4">
                      <Row className="g-3">
                        {/* Always show roles field first */}
                        {/* Show company field always */}
                        <Col sm={6} xl={5}>
                          <div>
                            <label className="form-label">
                              {t("user.company")}
                            </label>
                            <Controller
                              name="company"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  id="company"
                                  classNamePrefix="react-select"
                                  className={`react-select-container ${shouldShowError(errors.company) ? "is-invalid" : ""}`}
                                  value={companyList.find(
                                    (company) => company.value === field.value,
                                  )}
                                  onChange={(selectedOption) => {
                                    field.onChange(
                                      selectedOption
                                        ? selectedOption.value
                                        : null,
                                    ); // Set to null if cleared
                                    setValue("roles", []); // Clear roles when company changes
                                    if (isEdit || isSubmitted) trigger(); // Re-trigger validation only after first submit
                                  }}
                                  options={companyList}
                                  isClearable // Enable the clearable option
                                />
                              )}
                            />
                            {shouldShowError(errors.company) && (
                              <div className="invalid-feedback">
                                {t(errors.company?.message as string)}
                              </div>
                            )}
                          </div>
                        </Col>
                        <Col sm={6} xl={5}>
                          <div>
                            <label className="form-label">
                              {t("user.roles")}{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <Controller
                              name="roles"
                              control={control}
                              render={({ field }) => {
                                // Filter roles based on whether a company is selected (excluding ROLE_ADMIN - use AdminForm for that)
                                const allowedRoles = getValues("company")
                                  ? ["ROLE_COMPANY_ADMIN", "ROLE_COMPANY_USER"] // Roles allowed when a company is selected
                                  : ["ROLE_USER"]; // Roles allowed when no company is selected

                                const filteredRoles = userEnums?.filter(
                                  (role: any) => allowedRoles.includes(role),
                                );

                                return (
                                  <Select
                                    id="roles"
                                    classNamePrefix="react-select"
                                    className={`react-select-container ${shouldShowError(errors.roles) ? "is-invalid" : ""}`}
                                    value={
                                      field.value && field.value.length > 0
                                        ? {
                                            value: field.value[0],
                                            label: t(
                                              `enums.userRole.${field.value[0]}`,
                                            ),
                                          }
                                        : null
                                    }
                                    onChange={(selectedOption) => {
                                      // Always store as array with single value for backend compatibility
                                      field.onChange(
                                        selectedOption
                                          ? [selectedOption.value]
                                          : [],
                                      );
                                      // Re-trigger validation only after first submit
                                      if (isEdit || isSubmitted) {
                                        setTimeout(() => trigger(), 0);
                                      }
                                    }}
                                    options={filteredRoles?.map(
                                      (role: any) => ({
                                        label: t(`enums.userRole.${role}`),
                                        value: role,
                                      }),
                                    )}
                                    isClearable
                                  />
                                );
                              }}
                            />
                            {shouldShowError(errors.roles) && (
                              <div className="invalid-feedback">
                                {t(errors.roles?.message as string)}
                              </div>
                            )}
                          </div>
                        </Col>

                        {/* Only show other fields when roles are selected */}
                        {showBasicFields() && (
                          <>
                            <Col sm={6} xl={3}>
                              <div>
                                <label className="form-label">
                                  {t("user.firstName")}
                                  {selectedRoles.length > 0 && (
                                    <span className="text-danger">*</span>
                                  )}
                                </label>
                                <Controller
                                  name="firstName"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="text"
                                      className={`form-control ${shouldShowError(errors.firstName) ? "is-invalid" : ""}`}
                                      placeholder={t("user.firstName")}
                                      {...field}
                                    />
                                  )}
                                />
                                {shouldShowError(errors.firstName) && (
                                  <div className="invalid-feedback">
                                    {t(errors.firstName?.message as string)}
                                  </div>
                                )}
                              </div>
                            </Col>
                            <Col sm={6} xl={3}>
                              <div>
                                <label className="form-label">
                                  {t("user.lastName")}
                                  {selectedRoles.length > 0 && (
                                    <span className="text-danger">*</span>
                                  )}
                                </label>
                                <Controller
                                  name="lastName"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="text"
                                      className={`form-control ${shouldShowError(errors.lastName) ? "is-invalid" : ""}`}
                                      placeholder={t("user.lastName")}
                                      {...field}
                                    />
                                  )}
                                />
                                {shouldShowError(errors.lastName) && (
                                  <div className="invalid-feedback">
                                    {t(errors.lastName?.message as string)}
                                  </div>
                                )}
                              </div>
                            </Col>
                            <Col sm={6} xl={3}>
                              <div>
                                <label className="form-label">
                                  {t("user.email")}
                                  {selectedRoles.length > 0 && (
                                    <span className="text-danger">*</span>
                                  )}
                                </label>
                                <Controller
                                  name="email"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="email"
                                      className={`form-control ${shouldShowError(errors.email) ? "is-invalid" : ""}`}
                                      placeholder={t("user.email")}
                                      {...field}
                                    />
                                  )}
                                />
                                {shouldShowError(errors.email) && (
                                  <div className="invalid-feedback">
                                    {t(errors.email?.message as string)}
                                  </div>
                                )}
                              </div>
                            </Col>
                            <Col sm={6} xl={3}>
                              <div>
                                <label className="form-label">
                                  {t("user.phone")}
                                  {isAdmin() && (
                                    <span className="text-danger">*</span>
                                  )}
                                </label>
                                <Controller
                                  name="phone"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="text"
                                      className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                                      placeholder={t("user.phone")}
                                      {...field}
                                    />
                                  )}
                                />
                                {errors.phone && (
                                  <div className="invalid-feedback">
                                    {t(errors.phone.message as string)}
                                  </div>
                                )}
                              </div>
                            </Col>
                            <Col sm={6} xl={3}>
                              <div>
                                <label className="form-label">
                                  {t("user.city")}
                                </label>
                                <Controller
                                  name="city"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="text"
                                      className={`form-control ${errors.city ? "is-invalid" : ""}`}
                                      placeholder={t("user.city")}
                                      {...field}
                                      value={field.value ?? ""}
                                    />
                                  )}
                                />
                                {errors.city && (
                                  <div className="invalid-feedback">
                                    {t(errors.city.message as string)}
                                  </div>
                                )}
                              </div>
                            </Col>
                            {showAddressSuffix() && (
                              <Col sm={6} xl={3}>
                                <div>
                                  <label className="form-label">
                                    {t("user.addressSuffix")}
                                  </label>
                                  <Controller
                                    name="addressSuffix"
                                    control={control}
                                    render={({ field }) => (
                                      <input
                                        type="text"
                                        className={`form-control ${errors.addressSuffix ? "is-invalid" : ""}`}
                                        placeholder={t("user.addressSuffix")}
                                        {...field}
                                      />
                                    )}
                                  />
                                  {errors.addressSuffix && (
                                    <div className="invalid-feedback">
                                      {t(
                                        errors.addressSuffix.message as string,
                                      )}
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}
                            <Col sm={6} xl={3}>
                              <div>
                                <label className="form-label">
                                  {t("user.zip")}
                                </label>
                                <Controller
                                  name="zip"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="text"
                                      className={`form-control ${errors.zip ? "is-invalid" : ""}`}
                                      placeholder={t("user.zip")}
                                      {...field}
                                    />
                                  )}
                                />
                                {errors.zip && (
                                  <div className="invalid-feedback">
                                    {t(errors.zip.message as string)}
                                  </div>
                                )}
                              </div>
                            </Col>
                            <Col sm={6} xl={3}>
                              <div>
                                <label className="form-label">
                                  {t("user.address")}
                                </label>
                                <Controller
                                  name="address"
                                  control={control}
                                  render={({ field }) => (
                                    <input
                                      type="text"
                                      className={`form-control ${errors.address ? "is-invalid" : ""}`}
                                      placeholder={t("user.address")}
                                      {...field}
                                      value={field.value ?? ""}
                                    />
                                  )}
                                />
                                {errors.address && (
                                  <div className="invalid-feedback">
                                    {t(errors.address.message as string)}
                                  </div>
                                )}
                              </div>
                            </Col>
                          </>
                        )}

                        {showLocationTypes() && (
                          <Col sm={12} xl={4}>
                            <div>
                              <label className="form-label">
                                {t("job.locationType")}{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <Controller
                                name="locationType"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    isMulti
                                    classNamePrefix="react-select"
                                    className={`react-select-container ${
                                      errors.locationType ? "is-invalid" : ""
                                    }`}
                                    value={jobLocationType?.filter(
                                      (jobLocation: { value: string }) =>
                                        field.value.includes(jobLocation.value),
                                    )}
                                    onChange={(selectedOptions) =>
                                      field.onChange(
                                        selectedOptions
                                          ? selectedOptions.map(
                                              (option: any) => option.value,
                                            )
                                          : [],
                                      )
                                    }
                                    options={jobLocationType}
                                  />
                                )}
                              />
                              {errors.locationType && (
                                <div className="invalid-feedback">
                                  {t(errors.locationType.message as string)}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}

                        {showEmploymentType() && (
                          <Col sm={12} xl={4}>
                            <div>
                              <label className="form-label">
                                {t("user.employmentType")}{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <Controller
                                name="employmentType"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    classNamePrefix="react-select"
                                    className={`react-select-container ${errors.employmentType ? "is-invalid" : ""}`}
                                    value={
                                      employmentTypeOptions.find(
                                        (opt: any) => opt.value === field.value,
                                      ) || null
                                    }
                                    onChange={(selectedOption) =>
                                      field.onChange(
                                        selectedOption
                                          ? selectedOption.value
                                          : null,
                                      )
                                    }
                                    options={employmentTypeOptions}
                                  />
                                )}
                              />
                              {errors.employmentType && (
                                <div className="invalid-feedback">
                                  {t(errors.employmentType.message as string)}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}

                        {showTargetGroups() && (
                          <Col sm={12} xl={4}>
                            <div>
                              <label className="form-label">
                                {t("user.targetGroups")}{" "}
                                {isCoach() && (
                                  <span className="text-danger">*</span>
                                )}
                              </label>
                              <Controller
                                name="targetGroups"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    id="targetGroups"
                                    classNamePrefix="react-select"
                                    className={`react-select-container ${errors.targetGroups ? "is-invalid" : ""}`}
                                    isMulti={true}
                                    value={targetGroupList.filter((group) =>
                                      field.value.includes(group.value),
                                    )}
                                    onChange={(selectedOptions) =>
                                      field.onChange(
                                        selectedOptions
                                          ? selectedOptions?.map(
                                              (option) => option.value,
                                            )
                                          : [],
                                      )
                                    }
                                    options={targetGroupList}
                                  />
                                )}
                              />
                              {errors.targetGroups && (
                                <div className="invalid-feedback">
                                  {t(errors.targetGroups.message as string)}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}

                        {showQualifications() && (
                          <Col sm={12} xl={4}>
                            <div>
                              <label className="form-label">
                                {t("user.qualifications")}
                              </label>
                              <Controller
                                name="qualifications"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    id="qualifications"
                                    classNamePrefix="react-select"
                                    className={`react-select-container ${errors.qualifications ? "is-invalid" : ""}`}
                                    isMulti={true}
                                    value={qualificationList.filter((group) =>
                                      field.value.includes(group.value),
                                    )}
                                    onChange={(selectedOptions) =>
                                      field.onChange(
                                        selectedOptions
                                          ? selectedOptions?.map(
                                              (option) => option.value,
                                            )
                                          : [],
                                      )
                                    }
                                    options={qualificationList}
                                  />
                                )}
                              />
                              {errors.qualifications && (
                                <div className="invalid-feedback">
                                  {t(errors.qualifications.message as string)}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}

                        {showMinimumHourlyWage() && (
                          <Col sm={12} xl={4}>
                            <div>
                              <label className="form-label">
                                {t("user.minimumHourlyRate")}{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <Controller
                                name="minimumHourlyWage"
                                control={control}
                                render={({ field }) => (
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className={`form-control ${errors.minimumHourlyWage ? "is-invalid" : ""}`}
                                    placeholder={t("user.minimumHourlyRate")}
                                    {...field}
                                    value={field.value || ""}
                                    onChange={(e) =>
                                      field.onChange(
                                        e.target.value
                                          ? parseFloat(e.target.value)
                                          : undefined,
                                      )
                                    }
                                  />
                                )}
                              />
                              {errors.minimumHourlyWage && (
                                <div className="invalid-feedback">
                                  {t(
                                    errors.minimumHourlyWage.message as string,
                                  )}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}

                        {showExperience() && (
                          <Col sm={12} xl={4}>
                            <div>
                              <label className="form-label">
                                {t("user.experience")}{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <Controller
                                name="experience"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    id="experience"
                                    classNamePrefix="react-select"
                                    className={`react-select-container ${errors.experience ? "is-invalid" : ""}`}
                                    value={yearsOfExperienceOptions?.find(
                                      (option: any) =>
                                        option.value === field.value,
                                    )}
                                    onChange={(selectedOption) =>
                                      field.onChange(
                                        selectedOption
                                          ? selectedOption.value
                                          : "",
                                      )
                                    }
                                    options={yearsOfExperienceOptions}
                                    placeholder={t("user.selectExperience")}
                                  />
                                )}
                              />
                              {errors.experience && (
                                <div className="invalid-feedback">
                                  {t(errors.experience.message as string)}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}

                        {/* Status toggle - show for all users when roles are selected, positioned next to Experience */}
                        {showStatusToggle() && (
                          <Col sm={6} xl={4}>
                            <div>
                              <label className="form-label d-block">
                                {t("user.status")}
                              </label>
                              <Controller
                                name="isActive"
                                control={control}
                                render={({ field }) => (
                                  <div className="mt-2">
                                    <Switch
                                      id="isActive"
                                      className={`${errors.isActive ? "is-invalid" : ""}`}
                                      checked={field.value || false}
                                      onChange={(checked) =>
                                        field.onChange(checked)
                                      }
                                      label="" // Remove default label since we have our own above
                                    />
                                  </div>
                                )}
                              />
                              {errors.isActive && (
                                <div className="invalid-feedback">
                                  {t(errors.isActive.message as string)}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}

                        {!isEdit && showPasswordField() && (
                          <Col sm={6} xl={4}>
                            <div>
                              <label className="form-label">
                                {t("user.password")}
                              </label>
                              <Controller
                                name="password"
                                control={control}
                                render={({ field }) => (
                                  <input
                                    type="password"
                                    className={`form-control ${errors.password ? "is-invalid" : ""}`}
                                    placeholder={t("user.password")}
                                    {...field}
                                  />
                                )}
                              />
                              {errors.password && (
                                <div className="invalid-feedback">
                                  {t(errors.password.message as string)}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}
                        {showVideoPresentation() && (
                          <Col sm={12} xl={12}>
                            <div className="mb-0">
                              <label className="form-label">
                                {t("user.videoPresentation")}
                              </label>
                              <Controller
                                name="videoPresentation"
                                control={control}
                                render={({ field }) => (
                                  <textarea
                                    rows={5}
                                    className={`form-control ${errors.videoPresentation ? "is-invalid" : ""}`}
                                    placeholder={t("user.videoPresentation")}
                                    {...field}
                                    value={field.value || ""}
                                  />
                                )}
                              />
                              {errors.videoPresentation && (
                                <div className="invalid-feedback">
                                  {t(
                                    errors.videoPresentation.message as string,
                                  )}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}

                        {showTeachingLanguages() && (
                          <Col sm={10}>
                            <div className="mb-4">
                              <label className="form-label">
                                {t("user.teachingLanguages")}{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <Controller
                                name="teachingLanguages"
                                control={control}
                                render={({ field }) => (
                                  <>
                                    {field.value?.map(
                                      (item: any, index: number) => (
                                        <Row
                                          key={index}
                                          className="align-items-center mb-3"
                                        >
                                          <Col sm={5}>
                                            <Select
                                              id={`teachingLanguage-${index}`}
                                              classNamePrefix="react-select"
                                              className={`react-select-container ${errors.teachingLanguages?.[index]?.language ? "is-invalid" : ""}`}
                                              value={teachingLanguage.find(
                                                (lang: any) =>
                                                  lang.value === item.language,
                                              )}
                                              onChange={(selectedOption) => {
                                                const updatedLanguages = [
                                                  ...field.value,
                                                ];
                                                updatedLanguages[
                                                  index
                                                ].language = selectedOption
                                                  ? selectedOption.value
                                                  : "";
                                                field.onChange(
                                                  updatedLanguages,
                                                );
                                              }}
                                              options={teachingLanguage.filter(
                                                (lang: any) =>
                                                  !field.value.some(
                                                    (selected: any) =>
                                                      selected.language ===
                                                      lang.value,
                                                  ),
                                              )}
                                              placeholder={t(
                                                "user.selectLanguage",
                                              )}
                                            />
                                            {errors.teachingLanguages?.[index]
                                              ?.language && (
                                              <div className="invalid-feedback">
                                                {t(
                                                  errors.teachingLanguages[
                                                    index
                                                  ].language.message as string,
                                                )}
                                              </div>
                                            )}
                                          </Col>
                                          <Col sm={5}>
                                            <Select
                                              id={`languageSkillLevel-${index}`}
                                              classNamePrefix="react-select"
                                              className={`react-select-container ${errors.teachingLanguages?.[index]?.skillLevel ? "is-invalid" : ""}`}
                                              value={languageSkillLevel.find(
                                                (level: any) =>
                                                  level.value ===
                                                  item.skillLevel,
                                              )}
                                              onChange={(selectedOption) => {
                                                const updatedLanguages = [
                                                  ...field.value,
                                                ];
                                                updatedLanguages[
                                                  index
                                                ].skillLevel = selectedOption
                                                  ? selectedOption.value
                                                  : "";
                                                field.onChange(
                                                  updatedLanguages,
                                                );
                                              }}
                                              options={languageSkillLevel}
                                              placeholder={t(
                                                "user.selectProficiency",
                                              )}
                                            />
                                            {errors.teachingLanguages?.[index]
                                              ?.skillLevel && (
                                              <div className="invalid-feedback">
                                                {t(
                                                  errors.teachingLanguages[
                                                    index
                                                  ].skillLevel
                                                    .message as string,
                                                )}
                                              </div>
                                            )}
                                          </Col>
                                          <Col sm={2}>
                                            <Button
                                              variant="danger"
                                              onClick={() => {
                                                const updatedLanguages =
                                                  field.value.filter(
                                                    (_: any, i: number) =>
                                                      i !== index,
                                                  );
                                                field.onChange(
                                                  updatedLanguages,
                                                );
                                              }}
                                            >
                                              {t("common.remove")}
                                            </Button>
                                          </Col>
                                        </Row>
                                      ),
                                    )}
                                    <Button
                                      variant="primary"
                                      className={
                                        !field.value || field.value.length === 0
                                          ? "ms-2"
                                          : ""
                                      }
                                      onClick={() => {
                                        const updatedLanguages = [
                                          ...field.value,
                                          { language: "", skillLevel: "" },
                                        ];
                                        field.onChange(updatedLanguages);
                                      }}
                                    >
                                      {t("common.add")}
                                    </Button>
                                  </>
                                )}
                              />
                              {errors.teachingLanguages && (
                                <div className="invalid-feedback d-block">
                                  {t(
                                    errors.teachingLanguages.message as string,
                                  )}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}

                        {showCareerItems() && (
                          <Col sm={12}>
                            <div className="mb-4">
                              <label className="form-label">
                                {t("user.userCareerItems")}
                              </label>
                              <Controller
                                name="userCareerItems"
                                control={control}
                                render={({ field }) => (
                                  <Accordion
                                    activeKey={activeAccordionKey}
                                    onSelect={(key) =>
                                      setActiveAccordionKey(
                                        typeof key === "string" ? key : null,
                                      )
                                    }
                                  >
                                    {field.value?.map(
                                      (item: any, index: number) => (
                                        <Accordion.Item
                                          eventKey={index.toString()}
                                          key={index}
                                        >
                                          <Accordion.Header className="d-flex justify-content-between align-items-center">
                                            <span>
                                              {t("user.experience")} {index + 1}
                                            </span>
                                          </Accordion.Header>
                                          <Accordion.Body>
                                            <Row className="g-3">
                                              <Col sm={6}>
                                                <input
                                                  type="text"
                                                  className={`form-control ${errors.userCareerItems?.[index]?.location ? "is-invalid" : ""}`}
                                                  value={item.location || ""}
                                                  onChange={(e) => {
                                                    const updatedItems = [
                                                      ...field.value,
                                                    ];
                                                    updatedItems[
                                                      index
                                                    ].location = e.target.value;
                                                    field.onChange(
                                                      updatedItems,
                                                    );
                                                  }}
                                                  placeholder={t(
                                                    "user.location",
                                                  )}
                                                />
                                                {errors.userCareerItems?.[index]
                                                  ?.location && (
                                                  <div className="invalid-feedback">
                                                    {t(
                                                      errors.userCareerItems[
                                                        index
                                                      ].location
                                                        .message as string,
                                                    )}
                                                  </div>
                                                )}
                                              </Col>
                                              <Col sm={3}>
                                                <DatePicker
                                                  selected={
                                                    item.startDate
                                                      ? new Date(item.startDate)
                                                      : null
                                                  }
                                                  onChange={(date) => {
                                                    const updatedItems = [
                                                      ...field.value,
                                                    ];
                                                    updatedItems[
                                                      index
                                                    ].startDate =
                                                      date?.toISOString() || "";
                                                    field.onChange(
                                                      updatedItems,
                                                    );
                                                  }}
                                                  className={`form-control ${errors.userCareerItems?.[index]?.startDate ? "is-invalid" : ""}`}
                                                  placeholderText={t(
                                                    "user.startDate",
                                                  )}
                                                />
                                                {errors.userCareerItems?.[index]
                                                  ?.startDate && (
                                                  <div className="invalid-feedback">
                                                    {t(
                                                      errors.userCareerItems[
                                                        index
                                                      ].startDate
                                                        .message as string,
                                                    )}
                                                  </div>
                                                )}
                                              </Col>
                                              <Col sm={3}>
                                                <DatePicker
                                                  selected={
                                                    item.endDate
                                                      ? new Date(item.endDate)
                                                      : null
                                                  }
                                                  onChange={(date) => {
                                                    const updatedItems = [
                                                      ...field.value,
                                                    ];
                                                    updatedItems[
                                                      index
                                                    ].endDate =
                                                      date?.toISOString() || "";
                                                    field.onChange(
                                                      updatedItems,
                                                    );
                                                  }}
                                                  className={`form-control ${errors.userCareerItems?.[index]?.endDate ? "is-invalid" : ""}`}
                                                  placeholderText={t(
                                                    "user.endDate",
                                                  )}
                                                />
                                                {errors.userCareerItems?.[index]
                                                  ?.endDate && (
                                                  <div className="invalid-feedback">
                                                    {t(
                                                      errors.userCareerItems[
                                                        index
                                                      ].endDate
                                                        .message as string,
                                                    )}
                                                  </div>
                                                )}
                                              </Col>
                                              <Col sm={12}>
                                                <textarea
                                                  className={`form-control ${errors.userCareerItems?.[index]?.description ? "is-invalid" : ""}`}
                                                  value={item.description || ""}
                                                  onChange={(e) => {
                                                    const updatedItems = [
                                                      ...field.value,
                                                    ];
                                                    updatedItems[
                                                      index
                                                    ].description =
                                                      e.target.value;
                                                    field.onChange(
                                                      updatedItems,
                                                    );
                                                  }}
                                                  placeholder={t(
                                                    "user.description",
                                                  )}
                                                />
                                                {errors.userCareerItems?.[index]
                                                  ?.description && (
                                                  <div className="invalid-feedback">
                                                    {t(
                                                      errors.userCareerItems[
                                                        index
                                                      ].description
                                                        .message as string,
                                                    )}
                                                  </div>
                                                )}
                                              </Col>
                                              <Col className="text-end">
                                                {" "}
                                                <Button
                                                  variant="danger"
                                                  size="sm"
                                                  onClick={(e) => {
                                                    e.stopPropagation(); // Prevent accordion toggle
                                                    const updatedItems =
                                                      field.value.filter(
                                                        (_: any, i: number) =>
                                                          i !== index,
                                                      );
                                                    field.onChange(
                                                      updatedItems,
                                                    );
                                                    setActiveAccordionKey(null); // Reset active key after removal
                                                  }}
                                                >
                                                  {t("common.remove")}
                                                </Button>
                                              </Col>
                                            </Row>
                                          </Accordion.Body>
                                        </Accordion.Item>
                                      ),
                                    )}
                                    <Button
                                      variant="primary"
                                      className="mt-3"
                                      onClick={() => {
                                        const updatedItems = [
                                          ...field.value,
                                          {
                                            startDate: "",
                                            endDate: "",
                                            location: "",
                                            description: "",
                                          },
                                        ];
                                        field.onChange(updatedItems);
                                        setActiveAccordionKey(
                                          field.value.length.toString(),
                                        ); // Automatically open the newly added accordion
                                      }}
                                    >
                                      {t("common.add")}
                                    </Button>
                                  </Accordion>
                                )}
                              />
                              {errors.userCareerItems && (
                                <div className="invalid-feedback d-block">
                                  {t(errors.userCareerItems.message as string)}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}

                        {showAbout() && (
                          <Col sm={12} xl={12}>
                            <div>
                              <label className="form-label">
                                {t("user.about")}{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <div className={errors.about ? "is-invalid" : ""}>
                                <Controller
                                  name="about"
                                  control={control}
                                  render={({ field }) => (
                                    <Editor
                                      apiKey={
                                        process.env.REACT_APP_TINYMCE_API_KEY
                                      }
                                      value={field.value}
                                      onEditorChange={(content: string) =>
                                        field.onChange(content)
                                      }
                                      init={{
                                        height: 360,
                                        menubar: false,
                                        branding: false,
                                        plugins: [
                                          "advlist",
                                          "autolink",
                                          "lists",
                                          "link",
                                          "image",
                                          "charmap",
                                          "preview",
                                          "anchor",
                                          "searchreplace",
                                          "visualblocks",
                                          "code",
                                          "fullscreen",
                                          "insertdatetime",
                                          "media",
                                          "table",
                                          "help",
                                          "wordcount",
                                        ],
                                        toolbar:
                                          "undo redo | blocks | bold italic underline strikethrough | " +
                                          "alignleft aligncenter alignright alignjustify | " +
                                          "bullist numlist outdent indent | table | link image | removeformat | code",
                                        content_style:
                                          "body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }",
                                      }}
                                    />
                                  )}
                                />
                              </div>
                              {errors.about && (
                                <div className="invalid-feedback">
                                  {t(errors.about.message as string)}
                                </div>
                              )}
                            </div>
                          </Col>
                        )}
                        {/*<Col sm={12} xl={12}>*/}
                        {/*  <div>*/}
                        {/*    <label className="form-label">{t("user.teachingMethodAndPhilosophy")}</label>*/}
                        {/*    <div className={errors.teachingMethodAndPhilosophy ? 'is-invalid' : ''}>*/}
                        {/*      <Controller*/}
                        {/*        name="teachingMethodAndPhilosophy"*/}
                        {/*        control={control}*/}
                        {/*        render={({ field }) => (*/}
                        {/*          <Editor*/}
                        {/*            editorState={teachingMethodEditorState}*/}
                        {/*            onEditorStateChange={(editorState) => {*/}
                        {/*              setTeachingMethodEditorState(editorState);*/}
                        {/*              field.onChange(stateToHTML(editorState.getCurrentContent()));*/}
                        {/*            }}*/}
                        {/*            wrapperClassName="demo-wrapper"*/}
                        {/*            editorClassName={`form-control ${errors.teachingMethodAndPhilosophy ? 'is-invalid' : ''}`}*/}
                        {/*            toolbar={{*/}
                        {/*              options: ['inline', 'blockType', 'list', 'textAlign', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],*/}
                        {/*              inline: { options: ['bold', 'italic', 'underline', 'strikethrough'] },*/}
                        {/*              blockType: { options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'] },*/}
                        {/*              list: { options: ['unordered', 'ordered'] },*/}
                        {/*              textAlign: { options: ['left', 'center', 'right', 'justify'] },*/}
                        {/*              link: { options: ['link', 'unlink'] },*/}
                        {/*              embedded: { options: ['embedded'] },*/}
                        {/*              emoji: { options: ['emoji'] },*/}
                        {/*              image: { options: ['image'] },*/}
                        {/*              history: { options: ['undo', 'redo'] },*/}
                        {/*            }}*/}
                        {/*          />*/}
                        {/*        )}*/}
                        {/*      />*/}
                        {/*    </div>*/}
                        {/*    {errors.teachingMethodAndPhilosophy && (*/}
                        {/*      <div className="invalid-feedback">*/}
                        {/*        {t(errors.teachingMethodAndPhilosophy.message as string)}*/}
                        {/*      </div>*/}
                        {/*    )}*/}
                        {/*  </div>*/}
                        {/*</Col>*/}
                        {(showCategories() || showLocations()) && (
                          <Row className="mt-3">
                            {showCategories() && (
                              <Col sm={6}>
                                <div className="mb-4">
                                  <label className="form-label">
                                    {t("user.jobCategories")}{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <Controller
                                    name="jobCategories"
                                    control={control}
                                    render={({ field }) => (
                                      <>
                                        <div
                                          className={`rounded p-3 ${errors.jobCategories ? "border-danger" : ""}`}
                                          style={{
                                            minWidth: "300px",
                                            backgroundColor:
                                              "var(--bs-body-bg)",
                                            border: `1px solid ${errors.jobCategories ? "#dc3545" : "var(--bs-border-color)"}`,
                                          }}
                                        >
                                          <CheckboxTree
                                            nodes={categoryList}
                                            checked={checked}
                                            expanded={expanded}
                                            onCheck={(checkedCategories) => {
                                              setChecked(checkedCategories);
                                              field.onChange(
                                                checkedCategories.map(
                                                  (id: string) => parseInt(id),
                                                ),
                                              );
                                            }}
                                            onExpand={setExpanded}
                                            iconsClass="fa5"
                                          />
                                        </div>
                                        {errors.jobCategories && (
                                          <div className="invalid-feedback d-block">
                                            {t(
                                              errors.jobCategories
                                                .message as string,
                                            )}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  />
                                </div>
                              </Col>
                            )}
                            {showLocations() && (
                              <Col sm={6}>
                                <div className="mb-4">
                                  <label className="form-label">
                                    {t("user.locations")}
                                    {(watch("locationType")?.includes(
                                      "offline",
                                    ) ||
                                      watch("locationType")?.includes(
                                        "hybrid",
                                      )) &&
                                      (isCoach() || isAdmin()) && (
                                        <span className="text-danger">*</span>
                                      )}
                                  </label>
                                  <Controller
                                    name="locations"
                                    control={control}
                                    render={({ field }) => (
                                      <>
                                        <div
                                          className={`rounded p-3 ${errors.locations ? "border-danger" : ""}`}
                                          style={{
                                            minWidth: "300px",
                                            backgroundColor:
                                              "var(--bs-body-bg)",
                                            border: `1px solid ${errors.locations ? "#dc3545" : "var(--bs-border-color)"}`,
                                          }}
                                        >
                                          <CheckboxTree
                                            nodes={locationTree}
                                            checked={checkedLocations}
                                            expanded={expandedLocations}
                                            onCheck={(checkedLocs) => {
                                              setCheckedLocations(checkedLocs);
                                              field.onChange(
                                                checkedLocs.map((id: string) =>
                                                  parseInt(id),
                                                ),
                                              );
                                            }}
                                            onExpand={setExpandedLocations}
                                            iconsClass="fa5"
                                          />
                                        </div>
                                        {errors.locations && (
                                          <div className="invalid-feedback d-block">
                                            {t(
                                              errors.locations
                                                .message as string,
                                            )}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  />
                                </div>
                              </Col>
                            )}
                          </Row>
                        )}
                        <Row></Row>
                        {hasRoles() && (
                          <Row className="mt-3">
                            <Col sm={6}>
                              <div>
                                <label className="form-label">
                                  {t("user.image")}
                                </label>
                                <FormDropzone
                                  onFileUpload={handleImageUpload}
                                  existingFiles={
                                    getValues("image")
                                      ? [getValues("image")]
                                      : undefined
                                  }
                                  accept="image/*" // Accept only image files
                                />
                                {errors.image && (
                                  <div className="invalid-feedback d-block">
                                    {t("formErrors.imageRequired")}
                                  </div>
                                )}
                              </div>
                            </Col>
                            {showDocuments() && (
                              <Col sm={6}>
                                <div>
                                  <label className="form-label">
                                    {t("user.document")}
                                  </label>
                                  <FormDropzone
                                    onFileUpload={onDrop} // Handle file uploads
                                    shouldAppend={true} // Append new files to the existing ones
                                    existingFiles={getValues("documents")} // Pass the documents array directly
                                    deleteHandler={(file) => {
                                      const documentToDelete = getValues(
                                        "documents",
                                      )?.find((doc) => doc.id === file.id);

                                      if (documentToDelete) {
                                        setDocumentToDelete(
                                          documentToDelete as any,
                                        );
                                        setShowDeleteModal(true); // Show delete confirmation modal
                                      }
                                    }}
                                    multiple={true} // Allow multiple files
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar" // Accept only specific file types
                                  />
                                </div>

                                {/* Delete Confirmation Modal */}
                                <Modal
                                  show={showDeleteModal}
                                  onHide={() => setShowDeleteModal(false)}
                                >
                                  <Modal.Header closeButton>
                                    <Modal.Title>
                                      {t("user.deleteDocumentTitle")}
                                    </Modal.Title>
                                  </Modal.Header>
                                  <Modal.Body>
                                    {t("user.deleteDocumentMessage")}
                                  </Modal.Body>
                                  <Modal.Footer>
                                    <Button
                                      variant="secondary"
                                      onClick={() => setShowDeleteModal(false)}
                                    >
                                      {t("common.cancel")}
                                    </Button>
                                    <Button
                                      variant="danger"
                                      onClick={handleDeleteDocument}
                                    >
                                      {t("common.delete")}
                                    </Button>
                                  </Modal.Footer>
                                </Modal>
                              </Col>
                            )}
                          </Row>
                        )}

                        <Col sm={12} className="mt-4">
                          <Button type="submit" variant="primary">
                            {t(`user.${isEdit ? "update" : "create"}`)}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default UserForm;
